import { format } from 'date-fns';

import { UserAvatar } from 'components/user-avatar';
import { useAuth } from 'context/AuthContext';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { FaHeart } from 'react-icons/fa';
import { BsArrowReturnLeft } from 'react-icons/bs';
import { TbClockPause, TbMessageCancel, TbSearch } from 'react-icons/tb';

import { getKPISentHistoryPag } from 'services/dashboard.service';
import { convertEmojis } from 'utils/helpers';
import Humanograma from 'components/Humanograma';
import { IconLeafSeed, IconSprout } from 'components/rebrand/icons';
import { dimensaoByBehavior, hexToRgba, nivelByMoeda } from 'components/rebrand/mappings';

interface Props {
  filter?: any;
}

export default function Recognition(data: Props) {
  const { filter } = data;
  const { user, currentCycle } = useAuth();
  const [list, setList] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  const [load, setLoad] = useState(false);
  const [currentFilter, setCurrentFilter] = useState({});
  const searchRef = useRef<any>();
  const [loadingList, setLoadingList] = useState(true);

  function isEmAnalise(item: any) {
    return item?.em_analise === true;
  }

  function hasLike(item: any) {
    if (item?.attributes?.status !== 'OK') return false;
    return !!item?.interaction?.find((i: any) => i.player === user?._id && !!i.like);
  }

  function getComment(item: any) {
    if (item?.attributes?.status !== 'OK') return null;
    return item.interaction?.find((e: any) => !!e.message?.trim().length) || null;
  }

  function getReturnComment(item: any) {
    if (
      (item?.attributes?.status !== 'devolvido' && item?.attributes?.status !== 'cancelado') ||
      !item?.returnRecognition?.message
    ) {
      return null;
    }
    return item?.returnRecognition?.message;
  }

  useEffect(() => {
    setCurrentFilter(filter);
  }, [filter]);

  useEffect(() => {
    async function loadData() {
      setLoadingList(true);
      const rangeItem = page ? page * 5 : 0;

      if (page) setLoad(true);

      const data = await getKPISentHistoryPag(
        user?._id,
        currentFilter,
        rangeItem,
        5,
        currentCycle?.date_start,
        currentCycle?.date_finish,
      );
      setList(page ? [...list, ...data.result] : data.result);
      setTotal(data.range.total);
      setTotalPage(data.range.pages);

      setLoadingList(false);
      setLoad(false);
    }

    if (!!user && !!currentCycle) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCycle, user, page, currentFilter]);

  const onSearch = (e: FormEvent) => {
    setCurrentFilter({ ...currentFilter, ...{ search: searchRef.current.value } });
    e.preventDefault();
  };

  return (
    <>
      <div className="rb-feed-head">
        <h3 tabIndex={0}>
          Reconhecimentos enviados {total ? <span>({total})</span> : null}
        </h3>
        <form className="rb-feed-search" onSubmit={onSearch}>
          <span className="rb-feed-search__icon">
            <TbSearch size={16} />
          </span>
          <input
            className="rb-input rb-input--search"
            placeholder="Nome, matrícula, prefixo ou grupo"
            ref={(ref: any) => (searchRef.current = ref)}
            inputMode="search"
          />
        </form>
      </div>

      {loadingList && (
        <div className="rb-feed-grid">
          <div className="rb-skeleton" style={{ height: 220 }} />
          <div className="rb-skeleton" style={{ height: 220 }} />
        </div>
      )}

      {!loadingList && !list?.length && <div className="rb-empty">Nenhum reconhecimento enviado até o momento.</div>}

      {!loadingList && !!list?.length && (
        <>
          <div className="rb-feed-grid">
            {list.map((item: any) => {
              const emAnalise = isEmAnalise(item);
              const isOk = item?.attributes?.status === 'OK';
              const isEntregue = isOk && !emAnalise;
              const dim = dimensaoByBehavior(item.attributes.behavior);
              const nivel = nivelByMoeda(item.attributes.coin);
              const comment = getComment(item);
              const accent = isEntregue ? dim.color : emAnalise ? 'var(--color-review)' : 'var(--gray-250)';
              return (
                <article
                  className={`rb-feeditem ${isEntregue ? '' : emAnalise ? 'is-review' : 'is-muted'}`}
                  style={{ ['--acc' as any]: accent }}
                  key={item?._id}
                >
                  <div className="rb-feeditem__head">
                    <div className="rb-feeditem__avatar">
                      <UserAvatar data={{ ...item.target, ariaHidden: true }} responsive={true} />
                    </div>
                    <div className="rb-feeditem__who">
                      <Humanograma data={{ ...item, nome: item?.target?.name, funcao: item?.target?.funcao, _id: item?.attributes?.target }}>
                        <span className="rb-feeditem__name">
                          Você enviou para <b>{item.target.nome_guerra || item.target.name?.split(' ')[0]}</b>{' '}
                          <span className="rb-feeditem__key">({item.attributes.target})</span>
                        </span>
                      </Humanograma>
                      {!!item.target?.funcao && <div className="rb-feeditem__unit">{item.target.funcao}</div>}
                    </div>
                    <span className="rb-feeditem__date">{format(new Date(item.time), 'dd/MM - HH:mm')}</span>
                  </div>

                  <div className="rb-feeditem__chips">
                    {emAnalise && (
                      <span className="rb-chip rb-chip--review">
                        <TbClockPause size={14} /> Em análise
                      </span>
                    )}
                    {isOk ? (
                      <>
                        <span className="rb-nivelchip">
                          <IconSprout color="#2fa457" size={13} /> {nivel.tag}
                        </span>
                        <span
                          className="rb-dimchip"
                          style={{ color: dim.color, background: hexToRgba(dim.color, 0.1), boxShadow: `inset 0 0 0 1.5px ${dim.color}` }}
                        >
                          <span className="rb-dimchip__dot" style={{ background: dim.color }} />
                          {dim.label}
                        </span>
                        <span className="rb-sementeschip">
                          <IconLeafSeed color="var(--blue-primary)" size={13} /> {nivel.selos} selo{nivel.selos > 1 ? 's' : ''}
                        </span>
                      </>
                    ) : (
                      <span className="rb-chip rb-chip--danger">
                        {item?.attributes?.status === 'cancelado' ? <TbMessageCancel size={14} /> : <BsArrowReturnLeft size={14} />}{' '}
                        {item?.attributes?.status === 'cancelado' ? 'Reconhecimento cancelado' : 'Reconhecimento devolvido'}
                      </span>
                    )}
                  </div>

                  {emAnalise && (
                    <p className="rb-feeditem__review-note">
                      Este reconhecimento está em revisão e ainda não foi entregue ao seu colega. Os selos já saíram do seu
                      saldo e voltam caso ele não seja aprovado.
                    </p>
                  )}

                  {!!item.attributes.message && (
                    <>
                      <div className="rb-feeditem__label" style={{ color: isEntregue ? dim.color : emAnalise ? accent : undefined }}>
                        Reconhecimento enviado:
                      </div>
                      <p className="rb-feeditem__message" dangerouslySetInnerHTML={{ __html: convertEmojis(item.attributes.message) }} />
                    </>
                  )}

                  {!!comment && (
                    <div className="rb-feeditem__comment">
                      <div className="rb-feeditem__comment-head">
                        <span>Comentário de quem recebeu</span>
                      </div>
                      <div dangerouslySetInnerHTML={{ __html: convertEmojis(comment.message) }} />
                    </div>
                  )}

                  {!!getReturnComment(item) && (
                    <div className="rb-feeditem__comment">
                      <div className="rb-feeditem__comment-head">
                        <span>Motivo {item?.attributes?.status === 'cancelado' ? 'do cancelamento' : 'da devolução'}</span>
                      </div>
                      <div dangerouslySetInnerHTML={{ __html: convertEmojis(getReturnComment(item)) }} />
                    </div>
                  )}

                  {!!hasLike(item) && (
                    <div className="rb-feeditem__footer">
                      <span className="rb-social is-liked" style={{ cursor: 'default' }}>
                        <FaHeart size={15} /> Curtido
                      </span>
                    </div>
                  )}
                </article>
              );
            })}
          </div>

          {page + 1 < totalPage && (
            <div className="rb-feed-loadmore">
              <button type="button" className="rb-btn-primary" disabled={load} onClick={() => setPage(page + 1)}>
                {load ? 'Carregando...' : 'Carregar mais'}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}
